/**
 * Planning-chat user avatar — initials or profile image, right-aligned bubble companion.
 */

import React, { useEffect, useState } from 'react';
import { getUserProfilePictureUrl } from '@/utils/profileImageUrl';

function userInitials(name: string | null | undefined): string {
  const n = (name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  }
  return n.slice(0, 2).toUpperCase();
}

interface ChatUserAvatarProps {
  displayName?: string | null;
  avatarUrl?: string | null;
}

export const ChatUserAvatar: React.FC<ChatUserAvatarProps> = ({ displayName, avatarUrl }) => {
  const label = (displayName || '').trim() || 'User';
  const src = avatarUrl?.trim() || null;
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [src]);

  return (
    <div
      className="flex h-8 w-8 shrink-0 self-start items-center justify-center overflow-hidden rounded-full border border-[var(--color-border)] bg-[var(--color-secondary-bg)] text-xs font-semibold text-[var(--color-surface-text)]"
      title={label}
      aria-label={label}
    >
      {src && !imgFailed ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span aria-hidden>{userInitials(displayName)}</span>
      )}
    </div>
  );
};

/** Resolve avatar URL for the signed-in user (planning parity). */
export function avatarUrlForUser(user: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  profileImageUrl?: string | null;
  preferences?: { profile_image_url?: string | null };
} | null | undefined): string | null {
  if (!user) return null;
  return getUserProfilePictureUrl(user);
}
